// Vue
import { ref } from "vue";

// Dependencies
import { defineStore } from "pinia";

// Types
import { TransportTypeOptionType } from "../types/TransportTypeOptionType";

// GraphQL
import { Transport_Type_Enum, GetTransportTypeOptionsQuery } from "@/graphql";
import { TransportTypesType } from "@/features/BuildingProject";

interface TransportTypeOptionsProvider {
  // Getters
  getTransportTypeOptions: () => TransportTypeOptionType[];
  getDefaultTransportType: () => TransportTypeOptionType | null;

  // Setters
  setTransportTypeOptions: (newTransportTypeOptions: TransportTypeOptionType[]) => void;
  setDefaultTransportType: (newDefaultTransportType: TransportTypeOptionType) => void;

  // Methods
  getTransportTypeDataSource: (type: string) => string;
  getTransportOptionFromId: (id: string) => TransportTypeOptionType | undefined;
  getEmissionFromTransportType: (type: TransportTypesType) => number;
  mapData: (data: GetTransportTypeOptionsQuery) => void;

  // Utility
  getLoading: () => boolean;
  setLoading: (newValue: boolean) => void;
}

const useTransportTypeOptions = defineStore("TransportTypeOptionsProvider", (): TransportTypeOptionsProvider => {
  // ///////////////////
  // Refs
  // ///////////////////

  const transportTypeOptions = ref<TransportTypeOptionType[]>([]);
  const defaultTransportType = ref<TransportTypeOptionType>({} as TransportTypeOptionType);

  // ///////////////////
  // Getters
  // ///////////////////

  function getTransportTypeOptions() {
    return transportTypeOptions.value;
  }

  function getDefaultTransportType() {
    return defaultTransportType.value;
  }

  // ///////////////////
  // Setters
  // ///////////////////

  function setTransportTypeOptions(newTransportTypeOptions: TransportTypeOptionType[]) {
    transportTypeOptions.value = newTransportTypeOptions;
  }

  function setDefaultTransportType(newDefaultTransportType: TransportTypeOptionType) {
    defaultTransportType.value = newDefaultTransportType;
  }

  // ///////////////////
  // Methods
  // ///////////////////

  function mapData(data: GetTransportTypeOptionsQuery) {
    const { transport } = data;

    // Map all transport to TransportTypeOptionType
    if (!transport) return;

    // Map transport type options
    setTransportTypeOptions(transport);

    // Default transport type
    const defaultTransportType = transport.find((item) => item.isDefault);
    if (!defaultTransportType) return;
    setDefaultTransportType({ ...defaultTransportType, label: "Default" });
  }

  function getTransportTypeDataSource(id: string) {
    return transportTypeOptions.value.find((item) => item.id === id)?.datasource || "";
  }

  function getTransportOptionFromId(id: string) {
    return transportTypeOptions.value.find((item) => item.id === id);
  }

  // Helper to get emission from transport type or custom transport type
  function getEmissionFromTransportType(type: TransportTypesType) {
    // Return 0 if no transport type is selected
    if (!type) {
      return 0;
    }

    // Return emission from transport type if custom transport type is selected
    if (type.transport.transportType === Transport_Type_Enum.Custom) {
      return type.emission || 0;
    }

    // Return emission from transport type option else
    return getTransportOptionFromId(type.transport.id)?.emission || 0;
  }

  // ///////////////////
  // Utility
  // ///////////////////

  const loading = ref<boolean>(false);

  function setLoading(newValue: boolean) {
    loading.value = newValue;
  }

  function getLoading() {
    return loading.value;
  }

  // ///////////////////
  // Returns
  // ///////////////////

  return {
    // Getters
    getTransportTypeOptions,
    getDefaultTransportType,

    // Setters
    setTransportTypeOptions,
    setDefaultTransportType,

    // Methods
    getTransportTypeDataSource,
    getTransportOptionFromId,
    getEmissionFromTransportType,
    mapData,

    // Utility
    getLoading,
    setLoading,
  };
});

export default useTransportTypeOptions;
